<script setup lang="ts">
// One question of the inventory, stated and then answered: the id the design
// doc's table carries, the question in the operator's words, and a one-line sub
// that names what the panels measure — and, where a denominator exists, names
// the denominator itself, because a share without its denominator is not a
// finding.
//
// The answer is the slot: a question's panel(s) sit directly beneath it, so the
// reader never has to work out which chart belongs to which question. A question
// the data cannot answer says so in the same place, in its panel's own empty
// state, rather than being silently absent.
defineProps<{
	number?: string;
	question: string;
	sub: string;
}>();
</script>

<template>
	<section class="question">
		<h3 class="question-title">
			<span v-if="number" class="question-number">{{ number }}</span>
			{{ question }}
		</h3>
		<p class="question-sub">{{ sub }}</p>
		<div class="question-panels"><slot /></div>
	</section>
</template>

<style scoped>
.question {
	margin: 0 0 14px;
}

.question-title {
	display: flex;
	gap: 6px;
	margin: 0 0 2px;
	font-size: 0.82rem;
	font-weight: 600;
	color: #212529;
}

/* The id is tabular so the column of ids lines up down the report. */
.question-number {
	flex: 0 0 auto;
	color: #6c757d;
	font-variant-numeric: tabular-nums;
}

.question-sub {
	margin: 0 0 6px;
	font-size: 0.7rem;
	color: #6c757d;
}

/* The same cell grid the flat dashboard used, so a re-used panel needs no
   special case: a wide panel spans two cells, everything else takes one. */
.question-panels {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
	gap: 12px;
}
</style>
